import { memo, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Radio,
} from "@mui/material";
import { Close } from "@mui/icons-material";

import { ButtonV1, ModalBox } from "components";

import { validateNull, dateConvertor, timeConvertor } from "utils";
import { jodlShipmentMappingHeader } from "../../constant";
import { postCall_v2 } from "services";
import { useToaster } from "hooks";
import { success, error } from "constants";

const ShipmentSelectionModal = memo(
  ({ isOpen, onClose, selectedShipmentData, onSuccess, rowId }) => {
    const [selectedShipmentId, setSelectedShipmentId] = useState(null);
    const [loading, setLoading] = useState(false);
    const triggerToaster = useToaster();

    const handleMapShipment = async () => {
      try {
        const selectedShipment =
          selectedShipmentData?.shipmentDetailsJOTS?.[selectedShipmentId];

        if (!selectedShipment) {
          triggerToaster("Please select a shipment to map", error);
          return;
        }

        setLoading(true);
        const payload = {
          shipmentOrderLineItemAggregateId: rowId,
          shipmentNumberToBeMapped: selectedShipment?.shipmentNumber,
        };

        const response = await postCall_v2(
          "/oms/shipment/map-jots-shipment",
          payload,
        );
        triggerToaster("Shipment mapped successfully", success);
        onSuccess();
        onClose();
      } catch (err) {
        triggerToaster(
          err?.data?.errorMessage || "Failed to map shipment",
          error,
        );
      } finally {
        setLoading(false);
      }
    };

    return (
      <ModalBox width="70%" hideCrossIcon open={isOpen} onCloseModal={onClose}>
        <Box
          display={"flex"}
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Typography variant="h6">Select Shipment</Typography>
          <Close cursor="pointer" data-testid="CloseIcon" onClick={onClose} />
        </Box>
        <Divider />
        <Box mt={4}>
          <Typography mb={4}>Map the invoice with the shipment:</Typography>
          <TableContainer style={{ marginBottom: 14 }} component={Paper}>
            <Table aria-label="shipment selection table">
              <TableHead>
                <TableRow>
                  {jodlShipmentMappingHeader.map((header) => (
                    <TableCell key={header}>
                      <Typography fontSize={14} fontWeight="bold">
                        {header}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell colSpan={jodlShipmentMappingHeader.length}>
                    <Typography fontWeight={"bold"}>
                      Invoice details:
                    </Typography>
                  </TableCell>
                </TableRow>

                <TableRow key={selectedShipmentData?.invoiceDetailsJOTS?.id}>
                  <TableCell></TableCell>
                  <TableCell>
                    {validateNull(
                      selectedShipmentData?.invoiceDetailsJOTS?.vehicleDetails
                        ?.truckNumber,
                    )}
                  </TableCell>
                  <TableCell>
                    {selectedShipmentData?.invoiceDetailsJOTS?.createdAt &&
                      `${dateConvertor(
                        selectedShipmentData?.invoiceDetailsJOTS?.createdAt,
                      )} ${timeConvertor(
                        selectedShipmentData?.invoiceDetailsJOTS?.createdAt,
                      )}`}
                  </TableCell>
                  <TableCell>
                    {validateNull(
                      selectedShipmentData?.invoiceDetailsJOTS?.shipmentNumber,
                    )}
                  </TableCell>
                  <TableCell>
                    {validateNull(
                      selectedShipmentData?.invoiceDetailsJOTS?.sellerName,
                    )}
                  </TableCell>
                  <TableCell>
                    {validateNull(
                      selectedShipmentData?.invoiceDetailsJOTS?.pickupLocation
                        ?.address,
                    )}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell colSpan={jodlShipmentMappingHeader.length}>
                    <Typography fontWeight={"bold"}>
                      Shipment details:
                    </Typography>
                  </TableCell>
                </TableRow>
                {Array.isArray(selectedShipmentData?.shipmentDetailsJOTS) &&
                selectedShipmentData.shipmentDetailsJOTS.length > 0 ? (
                  selectedShipmentData.shipmentDetailsJOTS.map(
                    (shipment, index) => (
                      <TableRow key={shipment.id || index}>
                        <TableCell>
                          <Radio
                            checked={selectedShipmentId === index}
                            onChange={() => setSelectedShipmentId(index)}
                            value={index}
                            name="shipment-selection"
                            aria-label={`Select shipment`}
                            data-testid="shipment-radio"
                            inputProps={{ "aria-label": "Select shipment" }}
                          />
                        </TableCell>
                        <TableCell>
                          {validateNull(shipment?.vehicleDetails?.truckNumber)}
                        </TableCell>
                        <TableCell>
                          {shipment?.createdAt &&
                            `${dateConvertor(
                              shipment?.createdAt,
                            )} ${timeConvertor(shipment?.createdAt)}`}
                        </TableCell>
                        <TableCell>
                          {validateNull(shipment.shipmentNumber)}
                        </TableCell>
                        <TableCell>
                          {validateNull(shipment.sellerName)}
                        </TableCell>
                        <TableCell>
                          {validateNull(shipment.pickupLocation?.address)}
                        </TableCell>
                      </TableRow>
                    ),
                  )
                ) : (
                  <TableRow>
                    <TableCell colSpan={jodlShipmentMappingHeader.length}>
                      <Typography align="center">
                        No shipments available
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <ButtonV1
            boxStyle={{ justifyContent: "flex-end" }}
            title={"Create"}
            onClick={handleMapShipment}
            loader={loading}
            disabled={loading}
          />
        </Box>
      </ModalBox>
    );
  },
);

ShipmentSelectionModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedShipmentData: PropTypes.object.isRequired,
  onSuccess: PropTypes.func.isRequired,
  rowId: PropTypes.string.isRequired,
};

export default ShipmentSelectionModal;
